import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchData } from "./methods";
import { apiRoutes } from "./apiRoutes";
import { useAlert } from "./AlertContext";
import { TranslationsContext } from "./TranslationsContext";

const AUTH_TOKEN_CHANGED_EVENT = "auth-token-changed";

// gs.rest's roleMapper.js rewrites internal role codes to these public
// names on every response leaving gs.be (mapBackward in proxyCall.js), so
// `whoami` never actually delivers "xsscan"/"xsinfo" to the browser — it
// delivers these. Keep in sync with gs.rest/routes/roleMapper.js's roleMap.
// Public role names — gs.rest's roleMapper renames xsall/xsmain/... in every
// response, so grupa.main from /whoami arrives as these, never as "xs*".
export const VIEWER_ROLES = {
  XSALL: "administrator",
  XSMAIN: "moderator",
  XSSCAN: "obezbedjenje",
  XSINFO: "info_pult",
};

const ViewerContext = createContext({ role: null, isLoading: true });

// Who the logged-in Korisnik actually is (grupa.main), so the third-tier
// app can tell an xsmain/xsall admin apart from an xsscan/xsinfo account
// that's meant to see only a single reduced screen after logging into the
// same tier.
export const ViewerProvider = ({ children }) => {
  const [role, setRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authToken, setAuthToken] = useState(() =>
    typeof window !== "undefined" ? localStorage.getItem("authToken") : null,
  );
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { t } = useContext(TranslationsContext);

  useEffect(() => {
    const syncToken = () => setAuthToken(localStorage.getItem("authToken"));
    const onStorage = (e) => {
      if (e.key === "authToken" || e.key === null) syncToken();
    };
    window.addEventListener(AUTH_TOKEN_CHANGED_EVENT, syncToken);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(AUTH_TOKEN_CHANGED_EVENT, syncToken);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    if (!authToken) {
      setRole(null);
      setIsLoading(false);
      localStorage.removeItem("viewerRole");
      return;
    }

    setIsLoading(true);
    const fetchViewer = async () => {
      try {
        const result = await fetchData(apiRoutes.whoami, showAlert, navigate, t);
        const nextRole = result?.data?.grupa?.main || null;
        setRole(nextRole);
        // Mirrored to localStorage so contexts mounted *outside* the Router
        // (NotificationsProvider wraps ThirdApp, which is where
        // ViewerProvider actually lives) can still read the role without a
        // React context dependency they can't reach.
        if (nextRole) {
          localStorage.setItem("viewerRole", nextRole);
        } else {
          localStorage.removeItem("viewerRole");
        }
      } catch (err) {
        console.error("Error fetching viewer:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchViewer();
  }, [authToken]);

  return (
    <ViewerContext.Provider value={{ role, isLoading }}>
      {children}
    </ViewerContext.Provider>
  );
};

export const useViewer = () => useContext(ViewerContext);
